<script lang="ts" setup>
  import { useFormContext, getModelFromContext, setModelWithContext } from '@othercommerce/foundation';
  import { useId, computed } from 'vue';

  interface Props {
    id?: string | undefined;
    name?: string | undefined;
    label?: string | undefined;
    disabled?: boolean;
    readonly?: boolean;
    required?: boolean;
  }

  defineOptions({
    inheritAttrs: false,
  });

  const props = withDefaults(defineProps<Props>(), {
    disabled: false,
    readonly: false,
    required: false,
  });

  const context = useFormContext();
  const random = useId();

  const model = defineModel({
    get: (value) => getModelFromContext(props.name, context, value),
    set: (value) => setModelWithContext(props.name, context, value),
  });

  const id = computed(() => {
    return props.id ?? random;
  });

  const disabled = computed(() => {
    return Boolean(props.disabled || props.readonly || (context && context.readonly.value));
  });
</script>
<template>
  <SfControlWrapper v-bind="{ id, name, required }">
    <div :aria-required="required" aria-labelledby="stars__heading" class="control stars" role="radiogroup">
      <span id="stars__heading">{{ label }}{{ required ? '*' : null }}</span>
      <label :aria-label="$t('SHOP.COMMON.ARIA.STARS_ONE')" :for="`${id}-1`">
        <SfIconStar aria-hidden="true" class="star" />
      </label>
      <input :id="`${id}-1`" v-model="model" :value="1" type="radio" v-bind="{ name, disabled }" />
      <label :aria-label="$t('SHOP.COMMON.ARIA.STARS_TWO')" :for="`${id}-2`">
        <SfIconStar aria-hidden="true" class="star" />
      </label>
      <input :id="`${id}-2`" v-model="model" :value="2" type="radio" v-bind="{ name, disabled }" />
      <label :aria-label="$t('SHOP.COMMON.ARIA.STARS_THREE')" :for="`${id}-3`">
        <SfIconStar aria-hidden="true" class="star" />
      </label>
      <input :id="`${id}-3`" v-model="model" :value="3" type="radio" v-bind="{ name, disabled }" />
      <label :aria-label="$t('SHOP.COMMON.ARIA.STARS_FOUR')" :for="`${id}-4`">
        <SfIconStar aria-hidden="true" class="star" />
      </label>
      <input :id="`${id}-4`" v-model="model" :value="4" type="radio" v-bind="{ name, disabled }" />
      <label :aria-label="$t('SHOP.COMMON.ARIA.STARS_FIVE')" :for="`${id}-5`">
        <SfIconStar aria-hidden="true" class="star" />
      </label>
      <input :id="`${id}-5`" v-model="model" :value="5" type="radio" v-bind="{ name, disabled }" />
    </div>
  </SfControlWrapper>
</template>
<style lang="scss" scoped>
  .stars.control {
    display: flex;
    align-items: center;
    gap: calc(var(--sf-form-stars-size) / 8);
    font-size: var(--sf-form-stars-size);
    padding-top: var(--sf-input-floating-padding-top);
    padding-bottom: var(--sf-input-floating-padding-bottom);

    #stars__heading {
      position: absolute;
      left: 0;
      top: var(--sf-input-label-floating-top);
      display: inline-block;
      padding-left: var(--sf-input-label-padding-left);
      color: var(--sf-input-label-color);
      font-size: var(--sf-input-label-floating-font-size);
      line-height: var(--sf-input-label-line-height);
      margin: 0;
      padding-top: var(--sf-input-label-floating-padding-top);
      opacity: var(--sf-input-label-floating-opacity);
    }

    input {
      height: 0;
      width: 0;
      opacity: 0;
      display: contents;

      &:checked {
        & ~ label {
          color: var(--sf-stars-color-star, rgb(220, 220, 220));
        }
      }
    }

    label {
      color: var(--sf-stars-color-star-filled, rgb(240, 220, 0));
      cursor: pointer;
    }
  }
</style>
